import type { ReactElement } from 'react';
import { EditableText } from '../../../components/Editable';
import {
  audienceCardForContentRef,
  tacticForContentRef,
  tacticLabel,
} from '../content';
import { StudioBox, warnUnsupportedNode, type StudioNodeRendererProps } from './box';

/**
 * AudienceCard: `contentRef` = `audience.segment.<n>` (1-indexed). Reads the
 * segment the same way `AudienceProfilesPage`'s `audienceCards()` does
 * (`lib/audiencePresentation.ts`); a `fields` override on this section wins
 * for the card's own copy so a Studio slide can restate a persona without
 * mutating the shared audience segment every other slide also reads.
 */
export function AudienceCardNode({ node, ctx }: StudioNodeRendererProps): ReactElement | null {
  const { section, proposal, actions, readOnly } = ctx;
  const contentRef = node.props?.contentRef;
  const card = audienceCardForContentRef(proposal, contentRef);
  if (!card) {
    warnUnsupportedNode(node);
    return null;
  }
  const fields = section.content.fields ?? {};
  const nameKey = `${contentRef}.name`;
  const descKey = `${contentRef}.description`;
  const name = fields[nameKey] ?? card.name;
  const description = fields[descKey] ?? card.description;
  return (
    <StudioBox node={node} className="tpl-studio-audience-card">
      <EditableText
        value={name}
        field={`fields.${nameKey}`}
        placeholder="Segment name"
        readOnly={readOnly}
        variant="headline"
        clampLines={1}
        onSave={(next) => actions.saveContent(section, { fields: { ...fields, [nameKey]: next } })}
      />
      <EditableText
        value={description}
        field={`fields.${descKey}`}
        placeholder="Describe this audience"
        readOnly={readOnly}
        clampLines={2}
        onSave={(next) => actions.saveContent(section, { fields: { ...fields, [descKey]: next } })}
      />
    </StudioBox>
  );
}

/**
 * TacticCard: `contentRef` = `tactic:<productId>`. Reads the tactic's
 * strategic role from `proposal.strategy.tactics`, same source
 * `TacticAudiencePage`/`TacticGroupPage` read from.
 */
export function TacticCardNode({ node, ctx }: StudioNodeRendererProps): ReactElement | null {
  const { section, proposal, actions, readOnly } = ctx;
  const contentRef = node.props?.contentRef;
  const tactic = tacticForContentRef(proposal, contentRef);
  if (!tactic) {
    warnUnsupportedNode(node);
    return null;
  }
  const fields = section.content.fields ?? {};
  const roleKey = `${contentRef}.role`;
  const label = tacticLabel(proposal, tactic.productId);
  const role = fields[roleKey] ?? tactic.role;
  return (
    <StudioBox node={node} className="tpl-studio-tactic-card">
      <div className="tpl-studio-tactic-label">{label}</div>
      <EditableText
        value={role}
        field={`fields.${roleKey}`}
        placeholder="Strategic role"
        readOnly={readOnly}
        clampLines={2}
        onSave={(next) => actions.saveContent(section, { fields: { ...fields, [roleKey]: next } })}
      />
    </StudioBox>
  );
}

export function EvidenceCardNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}

export function QuoteCardNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}

export function ComparisonCardNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}

export function ProductModuleNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}
